import { area_type_enum, class_of_use_enum, type_of_use_enum } from '@predium/enums';
import { PreTFunction, translateClassOfUseEnum_dynamic, translateTypeOfUseEnum_dynamic } from '@predium/i18n/client';
import { accessEnum } from '@predium/utils';
import isNil from 'lodash/isNil';

/**
 * Sentinel dropdown values for the two "compare with" use-type options. Negative
 * so they never collide with a real portfolio id (always positive), the entire
 * company (0), or the null top item key (-1, private to PortfolioSelectionField).
 */
export const BENCHMARK_ENTIRE_COMPANY_ID = 0;
export const SAME_CLASS_OF_USE_VALUE = -2;
export const SAME_TYPE_OF_USE_VALUE = -3;

type AreaForMajorEbf = {
  id: number;
  value: number;
  area_type_id: string;
  class_of_use_id?: string | null;
  type_of_use_id?: string | null;
};

export type MajorEbfUse = {
  classOfUse: class_of_use_enum;
  typeOfUse: type_of_use_enum;
};

/**
 * The building's major EBF: the largest EBF area by value, ties broken by lowest
 * area id. Mirrors `majorEbfUseFilter` in building-analysis.service.ts so the
 * building always falls inside its own "same class/type of use" comparison.
 */
export function getMajorEbfUse(areas: AreaForMajorEbf[] | undefined): MajorEbfUse | undefined {
  if (isNil(areas)) return undefined;

  const ebfAreas = areas.filter((a) => a.area_type_id === area_type_enum.EBF);
  if (ebfAreas.length === 0) return undefined;

  const major = [...ebfAreas].sort((a, b) => b.value - a.value || a.id - b.id)[0];
  if (isNil(major) || isNil(major.class_of_use_id) || isNil(major.type_of_use_id)) return undefined;

  return {
    classOfUse: accessEnum(class_of_use_enum, major.class_of_use_id),
    typeOfUse: accessEnum(type_of_use_enum, major.type_of_use_id),
  };
}

/**
 * Label for the "whole asset base, same class of use" option. Spells out the
 * building's major class of use (e.g. "Total Asset Base Commercial").
 */
export function getSameClassOfUseBenchmarkLabel(t: PreTFunction, classOfUse: class_of_use_enum): string {
  return t('StatusQuoAnalysis_CompareWith_TotalAssetBaseClassOfUse', {
    classOfUse: translateClassOfUseEnum_dynamic(classOfUse),
  });
}

/**
 * Label for the "whole asset base, same type of use" option. Spells out the
 * building's major type of use (e.g. "Total Asset Base Hotel").
 */
export function getSameTypeOfUseBenchmarkLabel(t: PreTFunction, typeOfUse: type_of_use_enum): string {
  return t('StatusQuoAnalysis_CompareWith_TotalAssetBaseTypeOfUse', {
    typeOfUse: translateTypeOfUseEnum_dynamic(typeOfUse),
  });
}
